import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TABLET_MAX_WIDTH } from './breakpoints';

const OPEN_SECTIONS_KEY = 'sidebarOpenSections';
const EXPANDED_KEY = 'sidebarExpanded';

const isTabletOrSmaller = () => window.innerWidth <= TABLET_MAX_WIDTH;

const readPersistedOpenSections = () => {
  try {
    const saved = sessionStorage.getItem(OPEN_SECTIONS_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
};

const persistOpenSections = (sections) => {
  sessionStorage.setItem(OPEN_SECTIONS_KEY, JSON.stringify(sections));
};


const readPersistedExpanded = (isOpenSidebar) => {
  const saved = sessionStorage.getItem(EXPANDED_KEY);
  if (saved !== null) return saved === 'true';
  return !isTabletOrSmaller() && isOpenSidebar;
};

const persistExpanded = (value) => {
  sessionStorage.setItem(EXPANDED_KEY, String(value));
};


export function useSidebarState({ isOpenSidebar = true, handler = () => {} } = {}) {
  const [expanded, setExpandedState] = useState(() => readPersistedExpanded(isOpenSidebar));
  const [openSections, setOpenSections] = useState(readPersistedOpenSections);
  const wasTablet = useRef(isTabletOrSmaller());

  const setExpanded = useCallback(
    (next) => {
      setExpandedState(next);
      persistExpanded(next);
      handler(next);
    },
    [handler],
  );

  const toggleExpanded = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded, setExpanded]);

  const collapse = useCallback(() => {
    setExpanded(false);
  }, [setExpanded]);

 
  useEffect(() => {
    if (expanded !== isOpenSidebar) {
      handler(expanded);
    }
  }, []);

  // Auto-collapse when tablet size
  useEffect(() => {
    const handleResize = () => {
      const nowTablet = isTabletOrSmaller();
      if (nowTablet && !wasTablet.current) {
        setExpanded(false);
      }
      wasTablet.current = nowTablet;
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setExpanded]);

  const updateOpenSections = useCallback((updater) => {
    setOpenSections((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      persistOpenSections(next);
      return next;
    });
  }, []);

  const isOpenAt = useCallback((key) => !!openSections[key], [openSections]);

  
  const toggleSection = useCallback(
    (item, ctx) => {
      const forceOpen = item.forceOpenForRoles?.includes(ctx?.role);
      updateOpenSections((prev) => ({
        ...prev,
        [item.key]: forceOpen ? true : !prev[item.key],
      }));
    },
    [updateOpenSections],
  );

 
  const expandAndOpenPath = useCallback(
    (keyPath) => {
      setExpanded(true);
      updateOpenSections((prev) => {
        const next = { ...prev };
        keyPath.forEach((key) => {
          next[key] = true;
        });
        return next;
      });
    },
    [setExpanded, updateOpenSections],
  );

  return useMemo(
    () => ({ expanded, toggleExpanded, collapse, isOpenAt, toggleSection, expandAndOpenPath }),
    [expanded, toggleExpanded, collapse, isOpenAt, toggleSection, expandAndOpenPath],
  );
}
